import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  QrCode,
  Edit,
  Ban,
  ShieldCheck,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Tag,
  X,
  Loader2,
  CreditCard,
  CheckCircle2,
  PauseCircle,
  XCircle,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Receipt,
  Users,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { members, enrollments, plans as plansApi, billing } from '../api/endpoints.js'

const STATUS_BADGE = {
  active: 'badge-active',
  alert: 'badge-alert',
  suspended: 'badge-suspended',
  cancelled: 'badge-cancelled',
}

function StatusBadge({ status }) {
  return (
    <span className={STATUS_BADGE[status] ?? 'badge-cancelled'}>
      {status ?? 'unknown'}
    </span>
  )
}

const TABS = ['Overview', 'Enrollments', 'Check-ins', 'Billing']

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 text-text-dim mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-text-muted uppercase tracking-wide">{label}</p>
        <p className="text-sm text-text-primary">{value}</p>
      </div>
    </div>
  )
}

function QrModal({ memberId, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['member-qr', memberId],
    queryFn: () => members.getQrCode(memberId).then((r) => r.data),
  })

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-surface-2 border border-surface-3 rounded-2xl p-6 max-w-xs w-full text-center animate-fade-in">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-text-primary">Member QR Code</h3>
          <button onClick={onClose} className="text-text-dim hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <p className="text-red-400 text-sm py-4">Failed to load QR code.</p>
        ) : data?.qr_data_url ? (
          <img
            src={data.qr_data_url}
            alt="Member QR"
            className="w-48 h-48 mx-auto rounded-lg"
          />
        ) : (
          <div className="bg-surface-3 w-48 h-48 mx-auto rounded-lg flex items-center justify-center">
            <QrCode className="w-16 h-16 text-text-dim" />
          </div>
        )}
        <p className="text-text-muted text-xs mt-3">
          Member ID: {memberId}
        </p>
      </div>
    </div>
  )
}


// tags are stored as a JSON array in a TEXT column — tolerate both shapes
function parseTags(tags) {
  if (!tags) return []
  if (Array.isArray(tags)) return tags
  try {
    const parsed = JSON.parse(tags)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return String(tags).split(',').map((t) => t.trim()).filter(Boolean)
  }
}

function WaiverPanel({ member }) {
  const signUrl = `/widget/waiver?member_id=${member.id}`

  const copy = () => {
    navigator.clipboard?.writeText(`${window.location.origin}${signUrl}`)
      .then(() => toast.success('Signing link copied'))
      .catch(() => toast.error('Could not copy link'))
  }

  return (
    <div className="card md:col-span-2">
      <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
        Waiver
      </h3>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          {member.waiver_signed ? (
            <>
              <CheckCircle2 className="w-5 h-5 text-status-active" />
              <span className="text-text-primary">Signed and on file</span>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-status-alert" />
              <span className="text-text-primary">No waiver on file</span>
            </>
          )}
        </div>
        {!member.waiver_signed && (
          <div className="flex gap-2">
            <button onClick={copy} className="btn-secondary text-sm">Copy signing link</button>
            <a href={signUrl} target="_blank" rel="noreferrer" className="btn-primary text-sm">
              Sign now
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

function OverviewTab({ member }) {
  const fmtDate = (d) => {
    if (!d) return null
    try { return format(new Date(d), 'MMMM d, yyyy') } catch { return d }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="card">
        <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
          Contact
        </h3>
        <div className="divide-y divide-surface-3">
          <InfoRow icon={Mail} label="Email" value={member.email} />
          <InfoRow icon={Phone} label="Phone" value={member.phone} />
          <InfoRow
            icon={MapPin}
            label="Address"
            value={
              [member.address1, member.address2, member.city, member.state, member.zip_code]
                .filter(Boolean)
                .join(', ') || null
            }
          />
          <InfoRow
            icon={Calendar}
            label="Date of Birth"
            value={fmtDate(member.date_of_birth)}
          />
        </div>
      </div>
      <div className="card">
        <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
          Emergency Contact
        </h3>
        <div className="divide-y divide-surface-3">
          <InfoRow
            icon={User}
            label="Name"
            value={member.emergency_name}
          />
          <InfoRow
            icon={Phone}
            label="Phone"
            value={member.emergency_phone}
          />
          <InfoRow
            icon={Users}
            label="Relationship"
            value={member.emergency_relationship}
          />
        </div>
        {!member.emergency_name && (
          <p className="text-text-dim text-sm italic">No emergency contact on file.</p>
        )}
      </div>

      <WaiverPanel member={member} />
      {parseTags(member.tags).length > 0 && (
        <div className="card md:col-span-2">
          <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
            Tags
          </h3>
          <div className="flex flex-wrap gap-2">
            {parseTags(member.tags).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-3 py-1 bg-surface-3 border border-surface-4 rounded-full text-xs text-text-muted"
              >
                <Tag className="w-3 h-3" />
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}


const MEMBER_STATUSES = ['active', 'inactive', 'prospect', 'cancelled', 'suspended']

function MemberEditDialog({ member, onClose }) {
  const qc = useQueryClient()
  const [f, setF] = useState({
    first_name: member.first_name ?? '',
    last_name: member.last_name ?? '',
    email: member.email ?? '',
    phone: member.phone ?? '',
    address1: member.address1 ?? '',
    city: member.city ?? '',
    state: member.state ?? '',
    zip_code: member.zip_code ?? '',
    emergency_name: member.emergency_name ?? '',
    emergency_phone: member.emergency_phone ?? '',
    status: member.status ?? 'active',
    notes: member.notes ?? '',
    waiver_signed: !!member.waiver_signed,
    allow_24hr_access: !!member.allow_24hr_access,
  })
  const set = (k) => (e) =>
    setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  const save = useMutation({
    mutationFn: () => members.update(member.id, f),
    onSuccess: () => {
      toast.success('Member updated')
      qc.invalidateQueries({ queryKey: ['member', member.id] })
      qc.invalidateQueries({ queryKey: ['members'] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not update member'),
  })

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
         onClick={onClose}>
      <div className="card w-full max-w-lg animate-pop max-h-[88vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold">Edit member</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">First name</label>
            <input className="input-field" value={f.first_name} onChange={set('first_name')} /></div>
          <div><label className="label">Last name</label>
            <input className="input-field" value={f.last_name} onChange={set('last_name')} /></div>
          <div><label className="label">Email</label>
            <input type="email" className="input-field" value={f.email} onChange={set('email')} /></div>
          <div><label className="label">Phone</label>
            <input className="input-field" value={f.phone} onChange={set('phone')} /></div>
          <div className="col-span-2"><label className="label">Address</label>
            <input className="input-field" value={f.address1} onChange={set('address1')} /></div>
          <div><label className="label">City</label>
            <input className="input-field" value={f.city} onChange={set('city')} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">State</label>
              <input className="input-field" value={f.state} onChange={set('state')} /></div>
            <div><label className="label">ZIP</label>
              <input className="input-field" value={f.zip_code} onChange={set('zip_code')} /></div>
          </div>
          <div><label className="label">Emergency contact</label>
            <input className="input-field" value={f.emergency_name} onChange={set('emergency_name')} /></div>
          <div><label className="label">Emergency phone</label>
            <input className="input-field" value={f.emergency_phone} onChange={set('emergency_phone')} /></div>
          <div><label className="label">Status</label>
            <select className="input-field" value={f.status} onChange={set('status')}>
              {MEMBER_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select></div>
          <div className="flex items-end gap-4 pb-2">
            <label className="inline-flex items-center gap-2 text-sm text-text-muted cursor-pointer">
              <input type="checkbox" className="accent-[rgb(var(--primary))]"
                     checked={f.waiver_signed} onChange={set('waiver_signed')} />
              Waiver signed
            </label>
          </div>
          <div className="col-span-2">
            <label className="inline-flex items-center gap-2 text-sm text-text-muted cursor-pointer">
              <input type="checkbox" className="accent-[rgb(var(--primary))]"
                     checked={f.allow_24hr_access} onChange={set('allow_24hr_access')} />
              24-hour gate access
            </label>
          </div>
          <div className="col-span-2"><label className="label">Notes</label>
            <textarea rows="3" className="input-field" value={f.notes} onChange={set('notes')} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary inline-flex items-center gap-2"
                  disabled={save.isPending} onClick={() => save.mutate()}>
            <Save className="w-4 h-4" />{save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

function EnrollDialog({ memberId, onClose }) {
  const qc = useQueryClient()
  const [planId, setPlanId] = useState('')
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10))
  const [priceOverride, setPriceOverride] = useState('')
  const [notes, setNotes] = useState('')

  const { data } = useQuery({
    queryKey: ['plans'],
    queryFn: () => plansApi.list().then((r) => r.data),
  })
  const list = (Array.isArray(data) ? data : data?.items ?? []).filter((p) => p.is_active !== false)
  const selected = list.find((p) => p.id === planId)

  const create = useMutation({
    mutationFn: () => enrollments.create({
      member_id: memberId,
      plan_id: planId,
      start_date: startDate || null,
      price_override: priceOverride === '' ? null : Number(priceOverride),
      notes: notes || null,
    }),
    onSuccess: () => {
      toast.success('Member enrolled')
      qc.invalidateQueries({ queryKey: ['member-enrollments', memberId] })
      qc.invalidateQueries({ queryKey: ['member', memberId] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not create enrollment'),
  })

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
         onClick={onClose}>
      <div className="card w-full max-w-md animate-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold">Enroll in a plan</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="label">Plan</label>
        <select className="input-field mb-3" value={planId} onChange={(e) => setPlanId(e.target.value)}>
          <option value="">Choose a plan…</option>
          {list.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — ${Number(p.price).toFixed(2)}
              {p.billing_cycle ? ` / ${p.billing_cycle}` : ''}
            </option>
          ))}
        </select>

        {selected && (
          <div className="rounded-lg p-3 mb-3 text-xs text-text-muted"
               style={{ background: 'rgb(var(--surface-3) / .5)', border: '1px solid rgb(var(--border))' }}>
            <div className="flex justify-between"><span>Type</span><span>{(selected.type ?? '').replace('_', ' ')}</span></div>
            <div className="flex justify-between"><span>Price</span><span>${Number(selected.price).toFixed(2)}</span></div>
            {selected.signup_fee > 0 && (
              <div className="flex justify-between"><span>Signup fee</span><span>${Number(selected.signup_fee).toFixed(2)}</span></div>
            )}
            {selected.visits_included != null && (
              <div className="flex justify-between"><span>Visits included</span><span>{selected.visits_included}</span></div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">Start date</label>
            <input type="date" className="input-field" value={startDate}
                   onChange={(e) => setStartDate(e.target.value)} /></div>
          <div><label className="label">Price override</label>
            <input type="number" step="0.01" min="0" className="input-field" placeholder="optional"
                   value={priceOverride} onChange={(e) => setPriceOverride(e.target.value)} /></div>
        </div>
        <div className="mt-3"><label className="label">Notes</label>
          <input className="input-field" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={create.isPending || !planId}
                  onClick={() => create.mutate()}>
            {create.isPending ? 'Enrolling…' : 'Enroll member'}
          </button>
        </div>
      </div>
    </div>
  )
}

function EnrollmentsTab({ memberId }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['member-enrollments', memberId],
    queryFn: () => members.getEnrollments(memberId).then((r) => r.data),
  })

  const fmtDate = (d) => {
    if (!d) return '—'
    try { return format(new Date(d), 'MMM d, yyyy') } catch { return d }
  }

  const pause = useMutation({
    mutationFn: (id) => enrollments.pause(id, {}),
    onSuccess: () => {
      toast.success('Enrollment paused')
      qc.invalidateQueries(['member-enrollments', memberId])
    },
    onError: () => toast.error('Failed to pause enrollment'),
  })

  const cancel = useMutation({
    mutationFn: (id) => enrollments.cancel(id, {}),
    onSuccess: () => {
      toast.success('Enrollment cancelled')
      qc.invalidateQueries(['member-enrollments', memberId])
    },
    onError: () => toast.error('Failed to cancel enrollment'),
  })

  const reactivate = useMutation({
    mutationFn: (id) => enrollments.reactivate(id),
    onSuccess: () => {
      toast.success('Enrollment reactivated')
      qc.invalidateQueries(['member-enrollments', memberId])
    },
    onError: () => toast.error('Failed to reactivate enrollment'),
  })

  const [enrolling, setEnrolling] = useState(false)

  if (isLoading) return <div className="py-8 text-center text-text-dim">Loading enrollments…</div>
  const list = Array.isArray(data) ? data : data?.items ?? []

  return (
    <div className="space-y-4">
      {list.length === 0 && (
        <div className="card text-center py-8 text-text-dim">
          No enrollments found.
          <div className="mt-4">
            <button className="btn-primary inline-flex items-center gap-2" onClick={() => setEnrolling(true)}>
              <Plus className="w-4 h-4" /> Enroll in a plan
            </button>
          </div>
          {enrolling && <EnrollDialog memberId={memberId} onClose={() => setEnrolling(false)} />}
        </div>
      )}
      {list.map((e) => (
        <div key={e.id} className="card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="font-semibold text-text-primary">{e.plan_name ?? `Plan #${e.plan_id}`}</p>
                <StatusBadge status={e.status} />
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm text-text-muted mt-2">
                <span>Price: <span className="text-text-primary">${e.price}/{e.billing_cycle ?? 'mo'}</span></span>
                <span>Next bill: <span className="text-text-primary">{fmtDate(e.next_billing_date)}</span></span>
                <span>Started: <span className="text-text-primary">{fmtDate(e.started_at)}</span></span>
                {e.paused_until && (
                  <span>Paused until: <span className="text-yellow-400">{fmtDate(e.paused_until)}</span></span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {e.status === 'active' && (
                <button
                  onClick={() => pause.mutate(e.id)}
                  disabled={pause.isPending}
                  className="btn-secondary text-xs flex items-center gap-1"
                >
                  <PauseCircle className="w-3.5 h-3.5" /> Pause
                </button>
              )}
              {(e.status === 'active' || e.status === 'paused') && (
                <button
                  onClick={() => {
                    if (confirm('Cancel this enrollment?')) cancel.mutate(e.id)
                  }}
                  disabled={cancel.isPending}
                  className="btn-danger text-xs flex items-center gap-1"
                >
                  <XCircle className="w-3.5 h-3.5" /> Cancel
                </button>
              )}
              {(e.status === 'cancelled' || e.status === 'paused') && (
                <button
                  onClick={() => reactivate.mutate(e.id)}
                  disabled={reactivate.isPending}
                  className="btn-primary text-xs flex items-center gap-1"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Reactivate
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function CheckinsTab({ memberId }) {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useQuery({
    queryKey: ['member-checkins', memberId, page],
    queryFn: () =>
      members.getCheckins(memberId, { page, page_size: 20 }).then((r) => r.data),
    keepPreviousData: true,
  })

  const fmtDT = (d) => {
    if (!d) return '—'
    try { return format(new Date(d), 'MMM d, yyyy h:mm a') } catch { return d }
  }

  const list = Array.isArray(data) ? data : data?.items ?? []
  const total = data?.total ?? list.length
  const totalPages = Math.ceil(total / 20)

  if (isLoading) return <div className="py-8 text-center text-text-dim">Loading check-ins…</div>

  return (
    <div className="space-y-3">
      {list.length === 0 ? (
        <div className="card text-center py-8 text-text-dim">No check-ins found.</div>
      ) : (
        list.map((c, i) => (
          <div key={c.id ?? i} className="card flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-text-primary">{fmtDT(c.checked_in_at ?? c.created_at)}</p>
              {c.checked_out_at && (
                <p className="text-xs text-text-muted">Out: {fmtDT(c.checked_out_at)}</p>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-text-muted">
              {c.method && <span className="capitalize">{c.method}</span>}
              {c.checkin_type && <span className="capitalize">{c.checkin_type}</span>}
            </div>
          </div>
        ))
      )}
      {totalPages > 1 && (
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary text-xs px-3 py-1.5"
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary text-xs px-3 py-1.5"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

function BillingTab({ memberId }) {
  const money = (v) =>
    v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'

  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['member-billing', memberId],
    queryFn: () => billing.memberSummary(memberId).then((r) => r.data),
  })
  const { data: invData } = useQuery({
    queryKey: ['member-invoices', memberId],
    queryFn: () => billing.listInvoices({ member_id: memberId, page: 1, page_size: 20 }).then((r) => r.data),
  })
  const invoices = Array.isArray(invData) ? invData : invData?.items ?? []

  if (error) {
    return (
      <div className="card">
        <p className="font-semibold" style={{ color: 'rgb(var(--danger))' }}>Could not load billing</p>
        <p className="text-sm text-text-muted mt-1">
          {error?.response?.data?.detail || error.message}
        </p>
      </div>
    )
  }

  const tiles = [
    ['Lifetime value', summary?.lifetime_value ?? summary?.total_paid],
    ['Outstanding', summary?.outstanding ?? summary?.balance],
    ['Last payment', summary?.last_payment_amount],
  ]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {tiles.map(([label, val]) => (
          <div key={label} className="stat-card">
            <p className="text-xs uppercase tracking-wider text-text-muted font-medium">{label}</p>
            <p className="text-xl font-bold mt-0.5 tabular-nums">
              {isLoading ? <span className="skeleton inline-block h-6 w-20 align-middle" /> : money(val)}
            </p>
          </div>
        ))}
      </div>

      <div className="table-container">
        <table className="table-base">
          <thead className="table-head">
            <tr><th>Invoice</th><th>Amount</th><th>Status</th><th>Due</th></tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-text-dim">
                <Receipt className="w-7 h-7 mx-auto mb-2 opacity-30" />No invoices for this member.
              </td></tr>
            ) : invoices.map((inv) => (
              <tr key={inv.id} className="hover:bg-surface-3/60 transition-colors">
                <td className="px-4 py-3 text-sm font-mono text-text-muted">#{inv.id}</td>
                <td className="px-4 py-3 text-sm font-semibold tabular-nums">{money(inv.amount)}</td>
                <td className="px-4 py-3">
                  <span className={
                    inv.status === 'paid' ? 'badge-active'
                    : inv.status === 'failed' ? 'badge-suspended'
                    : inv.status === 'void' ? 'badge-cancelled' : 'badge-alert'}>
                    {inv.status ?? 'unknown'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-text-muted">
                  {inv.due_date ? format(new Date(inv.due_date), 'MMM d, yyyy') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function MemberDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [activeTab, setActiveTab] = useState('Overview')
  const [showQr, setShowQr] = useState(false)
  const [editing, setEditing] = useState(false)
  const [enrollOpen, setEnrollOpen] = useState(false)

  const { data: member, isLoading, error } = useQuery({
    queryKey: ['member', id],
    queryFn: () => members.get(id).then((r) => r.data),
  })

  const regenQr = useMutation({
    mutationFn: () => members.regenerateQr(id),
    onSuccess: () => {
      toast.success('New QR code issued')
      qc.invalidateQueries({ queryKey: ['member-qr', id] })
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not regenerate QR'),
  })

  const removeMember = useMutation({
    mutationFn: () => members.remove(id),
    onSuccess: () => {
      toast.success('Member deleted')
      qc.invalidateQueries({ queryKey: ['members'] })
      navigate('/members')
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not delete member'),
  })

  const blockMutation = useMutation({
    mutationFn: () =>
      member?.status === 'suspended'
        ? members.unblock(id)
        : members.block(id),
    onSuccess: () => {
      toast.success(
        member?.status === 'suspended' ? 'Member unblocked' : 'Member blocked',
      )
      qc.invalidateQueries(['member', id])
      qc.invalidateQueries(['members'])
    },
    onError: () => toast.error('Action failed'),
  })

  const fmtDate = (d) => {
    if (!d) return '—'
    try { return format(new Date(d), 'MMMM d, yyyy') } catch { return d }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 gap-3 text-text-muted">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading member…
      </div>
    )
  }

  if (error || !member) {
    return (
      <div className="text-center py-24">
        <p className="text-red-400 mb-4">Member not found or failed to load.</p>
        <button onClick={() => navigate('/members')} className="btn-secondary">
          Back to Members
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate('/members')}
        className="flex items-center gap-1.5 text-text-muted hover:text-text-primary text-sm transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Members
      </button>

      {/* Member Header */}
      <div className="card">
        <div className="flex flex-wrap items-start gap-5">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-2xl bg-surface-3 border border-surface-4 flex items-center justify-center text-2xl font-bold text-primary shrink-0">
            {member.first_name?.[0] ?? '?'}
            {member.last_name?.[0] ?? ''}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-[18rem]">
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-text-primary">
                {member.first_name} {member.last_name}
              </h1>
              <StatusBadge status={member.status} />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-text-muted">
              {member.email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" />
                  {member.email}
                </span>
              )}
              {member.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" />
                  {member.phone}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                Member since {fmtDate(member.member_since ?? member.created_at)}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <button
              onClick={() => setShowQr(true)}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <QrCode className="w-4 h-4" />
              QR Code
            </button>
            <button
              onClick={() => setEditing(true)}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={() => setEnrollOpen(true)}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              Enroll
            </button>
            <button
              onClick={() => {
                if (confirm('Issue a new QR code? The member\'s current code stops working immediately.')) {
                  regenQr.mutate()
                }
              }}
              disabled={regenQr.isPending}
              title="Regenerate QR code"
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${regenQr.isPending ? 'animate-spin' : ''}`} />
              New QR
            </button>
            <button
              onClick={() => {
                if (confirm(`Permanently delete ${member.full_name ?? 'this member'}? This cannot be undone.`)) {
                  removeMember.mutate()
                }
              }}
              disabled={removeMember.isPending}
              title="Delete member"
              aria-label="Delete member"
              className="p-2 rounded-lg text-text-dim hover:text-[rgb(var(--danger))] hover:bg-[rgb(var(--danger))]/12 transition-all duration-150 active:scale-95 disabled:opacity-40"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                if (
                  confirm(
                    member.status === 'suspended'
                      ? 'Unblock this member?'
                      : 'Block this member from gate access?',
                  )
                )
                  blockMutation.mutate()
              }}
              disabled={blockMutation.isPending}
              className={
                member.status === 'suspended'
                  ? 'btn-primary flex items-center gap-2 text-sm'
                  : 'btn-danger flex items-center gap-2 text-sm'
              }
            >
              {member.status === 'suspended' ? (
                <ShieldCheck className="w-4 h-4" />
              ) : (
                <Ban className="w-4 h-4" />
              )}
              {member.status === 'suspended' ? 'Unblock' : 'Block'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-surface-3 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors duration-150 ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'Overview' && <OverviewTab member={member} />}
      {activeTab === 'Enrollments' && <EnrollmentsTab memberId={id} />}
      {activeTab === 'Check-ins' && <CheckinsTab memberId={id} />}
      {activeTab === 'Billing' && <BillingTab memberId={id} />}

      {editing && <MemberEditDialog member={member} onClose={() => setEditing(false)} />}
      {enrollOpen && <EnrollDialog memberId={member.id} onClose={() => setEnrollOpen(false)} />}
      {showQr && <QrModal memberId={id} onClose={() => setShowQr(false)} />}
    </div>
  )
}
